// src/app/components/form-builder/FieldCard.tsx

import React, { useState } from "react";
import { Globe, GripVertical, ChevronDown, ChevronUp, Trash2, Copy } from "lucide-react";
import {
  FormField, FieldType, FieldWidth, LAYOUT_TYPES, CHOICE_TYPES,
  FIELD_PALETTE, SliderImage, MultiLangValue, FieldOptionML, mlGet, mlSet, mlWrap,
} from "./types";
import { DEFAULT_LANGUAGES, Language } from "@/types/multilang";
import { COUNTRY_NAMES } from "@/constants/countries";
import { AutoGrowTextarea } from "./AutoGrowTextarea";
import { MiniHtmlToolbar } from "./MiniHtmlToolbar";

// ─── Shared atoms ─────────────────────────────────────────────────────────────

const Inp: React.FC<React.InputHTMLAttributes<HTMLInputElement> & { mono?: boolean; maxLength?: number }> = ({ mono, className, maxLength, value, ...p }) => {
  const charCount = String(value || '').length;
  return (
    <div className="relative w-full group">
      <input
        {...p}
        value={value}
        maxLength={maxLength}
        className={`w-full px-3 py-2 text-xs border border-ink-200 rounded-xl bg-white font-sans focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all placeholder-gray-400 ${mono ? 'font-mono' : ''} ${className ?? ''}`}
      />
      {maxLength && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-ink-400 font-sans pointer-events-none bg-white px-1 rounded">
          {charCount}/{maxLength}
        </div>
      )}
    </div>
  );
};

const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, ...p }) => (
  <select {...p} className={`w-full px-3 py-2 text-xs border border-ink-200 rounded-lg bg-white font-sans focus:border-primary outline-none transition-colors ${className ?? ''}`} />
);

const Lbl: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-[10px] font-semibold text-ink-500 uppercase tracking-wider mb-1 font-sans">{children}</label>
);

const Row: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`mb-3 ${className ?? ''}`}>{children}</div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-wider border-b border-ink-200 pb-1.5 mb-3 mt-4">{children}</div>
);

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; sub?: string }> = ({ checked, onChange, label, sub }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <p className="text-xs font-medium text-ink-900 font-sans">{label}</p>
      {sub && <p className="text-[11px] text-ink-500 font-sans">{sub}</p>}
    </div>
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-primary' : 'bg-borderGreyColor'}`}
      style={{ width: 32, height: 18 }}>
      <span className={`absolute top-[2px] w-[14px] h-[14px] bg-white rounded-full shadow transition-all ${checked ? 'left-[16px]' : 'left-[2px]'}`} />
    </button>
  </div>
);

// ─── MultiLang Input ─────────────────────────────────────────────────────────
// Inline multilang input — one text input per active language tab.
// Same UX as the app builder's MultiLangInput but self-contained here.

const MultiLangInp: React.FC<{
  label: string;
  value: MultiLangValue | string | undefined;
  onChange: (v: MultiLangValue) => void;
  languages: Language[];
  activeLang: string;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
}> = ({ label, value, onChange, languages, activeLang, placeholder, multiline, maxLength }) => {
  const mv = mlWrap(value, languages[0]?.code ?? "en");
  const current = mv[activeLang] ?? "";

  const handleChange = (text: string) => {
    onChange(mlSet(mv, activeLang, text));
  };

  return (
    <Row>
      <div className="flex items-center justify-between mb-1">
        <Lbl>{label}</Lbl>
        <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded font-sans font-medium uppercase">
          {activeLang}
        </span>
      </div>
      {multiline ? (
        <AutoGrowTextarea
          value={current}
          onChange={e => handleChange(e.target.value)}
          placeholder={placeholder}
          showCount
          maxLength={maxLength}
        />
      ) : (
        <Inp value={current} onChange={e => handleChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} />
      )}
      {label === "Help Text" && <p className="text-[10px] text-ink-400 mt-1 font-sans">Shown below the field to guide users</p>}
    </Row>
  );
};

// ─── MultiLang Options (for radio/checkbox/select) ───────────────────────────

const MultiLangOptions: React.FC<{
  options: FieldOptionML[];
  onChange: (opts: FieldOptionML[]) => void;
  activeLang: string;
  languages: Language[];
  fieldType: FieldType; // needed to decide radio vs checkbox pre-select behaviour
}> = ({ options, onChange, activeLang, languages, fieldType }) => {
  const defaultLang = languages[0]?.code ?? "en";
  const isMulti = fieldType === "checkbox" || fieldType === "multiselect";

  const addOption = () => {
    const newLabel: MultiLangValue = {};
    languages.forEach(l => { newLabel[l.code] = `Option ${options.length + 1}`; });
    onChange([...options, { label: newLabel, selected: false }]);
  };

  const updateLabel = (i: number, text: string) => {
    onChange(options.map((o, idx) =>
      idx === i ? { ...o, label: mlSet(mlWrap(o.label, defaultLang), activeLang, text) } : o
    ));
  };

  // For radio/select: only one can be pre-selected — deselect all others
  // For checkbox/multiselect: toggle individually
  const toggleSelected = (i: number) => {
    onChange(options.map((o, idx) => {
      if (isMulti) {
        return idx === i ? { ...o, selected: !o.selected } : o;
      } else {
        return { ...o, selected: idx === i ? !o.selected : false };
      }
    }));
  };

  const removeOption = (i: number) => onChange(options.filter((_, j) => j !== i));

  const loadCountryList = () => {
    onChange(COUNTRY_NAMES.map(name => ({
      label: Object.fromEntries(languages.map(l => [l.code, name])),
      selected: false,
    })));
  };

  return (
    <Row>
      <div className="flex items-center justify-between mb-1">
        <Lbl>Options</Lbl>
        <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded font-sans font-medium uppercase">
          {activeLang}
        </span>
      </div>
      {fieldType === "select" && (
        <button
          type="button"
          onClick={loadCountryList}
          className="mb-2 flex items-center gap-1 text-[11px] text-primary font-sans font-medium hover:opacity-80"
        >
          <Globe className="h-3 w-3" /> Load country list
        </button>
      )}
      {/* Column headers */}
      <div className="flex items-center gap-2 px-1 mb-1">
        <span className="flex-1 text-[9px] text-ink-500 font-sans uppercase tracking-wide">Label</span>
        <span className="text-[9px] text-ink-500 font-sans uppercase tracking-wide w-16 text-center">Pre-select</span>
        <span className="w-5" />
      </div>
      <div className="space-y-1.5 mb-2">
        {options.map((o, i) => {
          const mv = mlWrap(o.label ?? {}, defaultLang);
          return (
            <div key={i} className="flex gap-2 items-center">
              <Inp
                value={mv[activeLang] ?? mv[defaultLang] ?? ""}
                onChange={e => updateLabel(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
              />
              {/* Pre-select toggle — radio for single, checkbox for multi */}
              <button
                type="button"
                title={o.selected ? "Remove pre-selection" : "Set as pre-selected"}
                onClick={() => toggleSelected(i)}
                className={`w-16 flex items-center justify-center flex-shrink-0 h-8 rounded-lg border transition-colors ${o.selected
                  ? "bg-primary border-primary text-white"
                  : "border-ink-200 text-ink-500 hover:border-primary hover:text-primary"
                  }`}
              >
                {isMulti
                  ? <span className="text-[10px]">{o.selected ? "☑" : "☐"}</span>
                  : <span className="text-[10px]">{o.selected ? "◉" : "○"}</span>
                }
              </button>
              <button onClick={() => removeOption(i)} className="p-1 rounded hover:bg-red-50 flex-shrink-0">
                <Trash2 className="h-3 w-3 text-red-400" />
              </button>
            </div>
          );
        })}
      </div>
      {/* Pre-selected summary */}
      {options.some(o => o.selected) && (
        <div className="mb-2 rounded-lg bg-primary/10 px-2.5 py-1.5 text-[10px] text-primary font-sans">
          Pre-selected: {options.filter(o => o.selected).map(o => mlGet(o.label ?? {}, activeLang) || mlGet(o.label ?? {}, defaultLang)).join(", ")}
        </div>
      )}
      <button onClick={addOption} className="flex items-center gap-1 text-xs text-primary font-medium font-sans hover:opacity-80">
        + Add Option
      </button>
    </Row>
  );
};

// ─── Field canvas preview ─────────────────────────────────────────────────────

function FieldPreview({ f, lang }: { f: FormField; lang: string }) {
  const baseInput = "w-full px-3 py-2 text-xs border border-ink-200 rounded-md bg-white pointer-events-none font-sans";
  const label = mlGet(f.label, lang);
  const ph = mlGet(f.placeholder, lang);

  if (f.type === 'textarea') return <textarea className={baseInput} rows={f.settings.rows ?? 3} placeholder={ph} readOnly />;

  if (f.type === 'select' || f.type === 'multiselect') return (
    <select className={baseInput} multiple={f.type === 'multiselect'} disabled>
      {f.type === 'select' && <option value="">{ph || 'Select...'}</option>}
      {f.options.map((o, i) => {
        const opt = o as FieldOptionML;
        const lbl = mlGet(opt.label ?? (o as any), lang);
        return <option key={i} selected={!!opt.selected}>{lbl}</option>;
      })}
    </select>
  );

  if (f.type === 'radio') return (
    <div className="flex flex-col gap-1.5">
      {f.options.map((o, i) => {
        const opt = o as FieldOptionML;
        const lbl = mlGet(opt.label ?? (o as any), lang);
        return (
          <label key={i} className="flex items-center gap-2 text-xs text-ink-900 font-sans cursor-default">
            <input type="radio" disabled defaultChecked={!!opt.selected} className="accent-primary" /> {lbl}
          </label>
        );
      })}
    </div>
  );

  if (f.type === 'checkbox') return (
    <div className="flex flex-col gap-1.5">
      {f.options.map((o, i) => {
        const opt = o as FieldOptionML;
        const lbl = mlGet(opt.label ?? (o as any), lang);
        return (
          <label key={i} className="flex items-center gap-2 text-xs text-ink-900 font-sans cursor-default">
            <input type="checkbox" disabled defaultChecked={!!opt.selected} className="accent-primary" /> {lbl}
          </label>
        );
      })}
    </div>
  );

  if (f.type === 'rating') return (
    <div className="flex gap-1">
      {Array.from({ length: f.settings.maxStars ?? 5 }).map((_, i) => (
        <span key={i} className="text-amber-400 text-lg leading-none">★</span>
      ))}
    </div>
  );

  if (f.type === 'slider') {
    const { min = 0, max = 100, step = 1, unit = '' } = f.settings;
    return (
      <div>
        <input type="range" min={min} max={max} step={step} defaultValue={min} className="w-full" style={{ accentColor: 'primary' }} />
        <div className="flex justify-between text-[10px] text-ink-500 mt-0.5">
          <span>{min}{unit}</span><span>{max}{unit}</span>
        </div>
      </div>
    );
  }

  if (f.type === 'file') return (
    <div>
      <div className="border-2 border-dashed border-ink-200 rounded-md p-3 text-center text-xs text-ink-500 font-sans mb-2">
        📎 {f.settings.multiple ? 'Drop files or click to upload' : 'Drop file or click to upload'}
      </div>
      {(f.settings.enableGoogleDrive || f.settings.enableDropbox) && (
        <div className="flex gap-2 flex-wrap">
          {f.settings.enableGoogleDrive && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium font-sans"
              style={{ background: '#e8f0fe', color: '#1a73e8' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M6.18 15L3.75 19h16.5l-2.43-4H6.18zM12 3L2 19h4.75L12 9.5 17.25 19H22L12 3z" /></svg>
              Google Drive
            </span>
          )}
          {f.settings.enableDropbox && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium font-sans"
              style={{ background: '#e8f4fb', color: '#0061fe' }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L6 6.5 12 11 6 15.5 0 11l6-4.5L0 2 6 6.5 12 2zM18 2l-6 4.5L18 11l-6 4.5L18 20l6-4.5L18 11l6-4.5L18 2z" /></svg>
              Dropbox
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (f.type === 'signature') return (
    <div className="border border-ink-200 rounded-md h-14 flex items-center justify-center text-xs text-ink-500 font-sans bg-gray-50">
      ✍ Draw signature here
    </div>
  );

  if (f.type === 'hidden') return (
    <div className="text-[10px] text-ink-500 italic bg-ink-50 rounded px-2 py-1.5 font-sans">
      Hidden field — value: {f.defaultValue || '(empty)'}
    </div>
  );

  if (f.type === 'terms_checkbox') return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" disabled className="mt-0.5 accent-primary flex-shrink-0" />
      <span
        className="text-xs text-ink-500 font-sans leading-relaxed"
        dangerouslySetInnerHTML={{ __html: f.settings.termsText ?? 'I agree to the Terms and Conditions' }}
      />
    </label>
  );

  const inputType: Record<string, string> = {
    email: 'email', phone: 'tel', number: 'number', url: 'url',
    password: 'password', date: 'date', time: 'time',
    datetime: 'datetime-local', date_range: 'date',
  };

  return <input type={inputType[f.type] ?? 'text'} className={baseInput} placeholder={ph} readOnly />;
}

// ─── Field Settings Panel ─────────────────────────────────────────────────────

const HtmlEditorBlock: React.FC<{ value: string; onChange: (v: string) => void; activeLang: string }> = ({ value, onChange, activeLang }) => {
  const [showPreview, setShowPreview] = useState(false);
  return (
    <Row>
      <div className="flex items-center justify-between mb-1">
        <Lbl>HTML Content</Lbl>
        <div className="flex items-center gap-2">
          <div className="flex bg-ink-100 rounded-md p-0.5">
            <button type="button" onClick={() => setShowPreview(false)} className={`text-[10px] px-2 py-0.5 rounded-sm transition-colors ${!showPreview ? 'bg-white text-ink-900 shadow-sm font-semibold' : 'text-ink-500 hover:text-ink-700'}`}>Edit</button>
            <button type="button" onClick={() => setShowPreview(true)} className={`text-[10px] px-2 py-0.5 rounded-sm transition-colors ${showPreview ? 'bg-white text-ink-900 shadow-sm font-semibold' : 'text-ink-500 hover:text-ink-700'}`}>Preview</button>
          </div>
          <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded font-sans font-medium uppercase ml-1">
            {activeLang}
          </span>
        </div>
      </div>
      {showPreview ? (
        <div className="w-full px-3 py-2.5 text-xs border border-ink-200 rounded-xl bg-white min-h-[48px] overflow-auto" dangerouslySetInnerHTML={{ __html: value || '<span class="text-ink-300 italic">Empty</span>' }} />
      ) : (
        <AutoGrowTextarea
          value={value}
          onChange={e => onChange(e.target.value)}
          className="font-mono bg-slate-50 text-slate-800"
        />
      )}
      <p className="text-[10px] text-ink-400 mt-1 font-sans">Supports basic HTML like &lt;a&gt;, &lt;b&gt;, &lt;br&gt;</p>
    </Row>
  );
};

const TermsEditorBlock: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  return (
    <Row>
      <div className="flex items-center justify-between mb-1">
        <Lbl>Checkbox Label / HTML</Lbl>
      </div>
      <MiniHtmlToolbar textareaRef={ref} onChange={onChange} />
      <AutoGrowTextarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="I agree to the <a href='/terms'>Terms</a> and <a href='/privacy'>Privacy Policy</a>"
      />
      <p className="text-[10px] text-ink-500 mt-1 font-sans">HTML is supported — use &lt;a href='...'&gt; for links</p>
    </Row>
  );
};

const FieldSettingsPanelInner: React.FC<{
  field: FormField;
  allFields: FormField[];
  onChange: (f: FormField) => void;
  onDelete: () => void;
  languages: Language[];
  activeLang: string;
  isMultiStep?: boolean;
}> = ({ field: f, allFields, onChange, onDelete, languages, activeLang, isMultiStep }) => {
  const [tab, setTab] = useState<'basic' | 'validation' | 'conditions' | 'advanced'>('basic');
  const isLayout = LAYOUT_TYPES.includes(f.type);
  const hasOpts = CHOICE_TYPES.includes(f.type);

  const set = (k: keyof FormField, v: any) => onChange({ ...f, [k]: v });
  const setV = (k: string, v: any) => onChange({ ...f, validation: { ...f.validation, [k]: v } });
  const setS = (k: string, v: any) => onChange({ ...f, settings: { ...f.settings, [k]: v } });
  const setML = (k: keyof FormField, v: MultiLangValue) => onChange({ ...f, [k]: v });

  const addSliderImg = () => setS('sliderImages', [...(f.settings.sliderImages ?? []), { label: '', img: '', value: 0 }]);
  const updSliderImg = (i: number, k: keyof SliderImage, v: any) => {
    const imgs = [...(f.settings.sliderImages ?? [])];
    imgs[i] = { ...imgs[i], [k]: v };
    setS('sliderImages', imgs);
  };

  const TABS = ['basic', 'validation', 'conditions', 'advanced'] as const;

  // Language selector in the settings panel header
  const langBadge = languages.length > 1 ? (
    <div className="flex items-center gap-1 px-3 py-2 border-b border-ink-200 bg-primary/20 flex-shrink-0">
      <Globe className="h-3 w-3 text-primary" />
      <span className="text-[10px] text-ink-500 font-sans">Editing:</span>
      <span className="text-[10px] font-bold text-primary font-sans uppercase">
        {languages.find(l => l.code === activeLang)?.label ?? activeLang}
      </span>
      <span className="text-[10px] text-ink-500 font-sans ml-1">
        — switch language in top bar
      </span>
    </div>
  ) : null;

  return (
    <div className="flex flex-col h-full bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-ink-200 bg-surface flex-shrink-0">
        <div>
          <p className="text-sm font-bold text-ink-900 font-sans capitalize tracking-tight">{f.type} Field</p>
          <p className="text-[10px] text-ink-500 font-mono mt-0.5">{`{${f.name}}`}</p>
        </div>
        <button onClick={onDelete} className="p-2 rounded-lg hover:bg-danger-50 text-ink-400 hover:text-danger-600 transition-colors border border-transparent hover:border-danger-100">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Language indicator */}
      {languages.length > 1 && (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-ink-100 bg-brand-50/30 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5 text-brand-600" />
            <span className="text-[10px] font-medium text-ink-500 font-sans">Editing Language:</span>
          </div>
          <span className="text-[10px] font-bold text-brand-700 bg-brand-100 border border-brand-200 px-2 py-0.5 rounded-md font-sans uppercase">
            {languages.find(l => l.code === activeLang)?.label ?? activeLang}
          </span>
        </div>
      )}

      {/* Sub-tabs */}
      {!isLayout && (
        <div className="flex px-4 pt-4 pb-2 border-b border-ink-100 flex-shrink-0 gap-1 bg-surface">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-1.5 px-2 text-[10px] font-bold capitalize transition-all rounded-md font-sans ${tab === t
                ? 'text-brand-700 bg-brand-100 shadow-sm'
                : 'text-ink-500 bg-transparent hover:bg-ink-100 hover:text-ink-900'
                }`}>
              {t}
            </button>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">

        {/* ── BASIC ── */}
        {(tab === 'basic' || isLayout) && (
          <>
            {f.type !== 'divider' && f.type !== 'spacer' && (
              <MultiLangInp
                label={isLayout ? 'Content' : 'Label'}
                value={f.label}
                onChange={v => setML('label', v)}
                languages={languages}
                activeLang={activeLang}
                placeholder="Field label"
                multiline={f.type === 'textarea'}
              />
            )}

            {/* ── Divider: section grouping settings ── */}
            {f.type === 'divider' && (
              <>
                <div className="bg-primary/60 border border-primary/20 rounded-lg px-3 py-2.5 mb-3 text-[10px] text-primary font-sans leading-relaxed">
                  {isMultiStep ? (
                    <><strong>Step break</strong> — fields above belong to the previous step, fields below start a new step. Add a label to title this step.</>
                  ) : (
                    <><strong>Section divider</strong> — fields above appear in one card, fields below appear in the next card. Add a label to title each section.</>
                  )}
                </div>
                <Row>
                  <Lbl>{isMultiStep ? 'Step Name (optional)' : 'Section Label (optional)'}</Lbl>
                  <Inp
                    value={f.settings.sectionLabel ?? ''}
                    onChange={e => setS('sectionLabel', e.target.value)}
                    placeholder="e.g. Personal Details, Payment Info…"
                  />
                  <p className="text-[10px] text-ink-500 mt-1 font-sans">
                    {isMultiStep ? 'Shown in the step progress bar and above the step on the form.' : 'Shown as heading above the section box on the form.'}
                  </p>
                </Row>
                {isMultiStep && (
                  <Row>
                    <Lbl>Step Icon (optional)</Lbl>
                    <Inp
                      value={f.settings.sectionIcon ?? ''}
                      onChange={e => setS('sectionIcon', e.target.value.slice(0, 4))}
                      placeholder="e.g. 🚚 📦 💳 — defaults to the step number"
                    />
                    <p className="text-[10px] text-ink-500 mt-1 font-sans">Paste an emoji to show it in the step's progress dot instead of the step number.</p>
                  </Row>
                )}
                <Row>
                  <Lbl>Section Style</Lbl>
                  <div className="flex gap-2 mt-1">
                    {([
                      { v: 'card', label: 'Card', desc: 'White card with shadow' },
                      { v: 'bordered', label: 'Bordered', desc: 'Simple border' },
                      { v: 'plain', label: 'Plain', desc: 'No box, just spacing' },
                    ] as const).map(opt => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setS('sectionStyle', opt.v)}
                        title={opt.desc}
                        className={`flex-1 py-2 rounded-lg border text-[10px] font-semibold font-sans transition-all ${(f.settings.sectionStyle ?? 'card') === opt.v
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-ink-200 text-ink-500 hover:border-primary/40'
                          }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </Row>
              </>
            )}

            {f.type === 'spacer' && (
              <Row><Lbl>Height (px)</Lbl><Inp type="number" value={f.settings.height ?? 20} onChange={e => setS('height', +e.target.value)} /></Row>
            )}

            {f.type === 'html' && (
              <HtmlEditorBlock
                value={mlGet(f.settings.html, activeLang) || ''}
                onChange={v => setS('html', mlSet(f.settings.html, activeLang, v))}
                activeLang={activeLang}
              />
            )}

            {f.type === 'terms_checkbox' && (
              <>
                <TermsEditorBlock
                  value={f.settings.termsText ?? ''}
                  onChange={v => setS('termsText', v)}
                />
                <Row>
                  <Lbl>Link to full T&amp;C page (optional)</Lbl>
                  <Inp
                    type="url"
                    value={f.settings.termsUrl ?? ''}
                    onChange={e => setS('termsUrl', e.target.value)}
                    placeholder="https://yourstore.com/terms"
                  />
                  <p className="text-[10px] text-ink-500 mt-1 font-sans">If set, the label text becomes a clickable link</p>
                </Row>
              </>
            )}

            {!isLayout && (
              <>
                <Row>
                  <Lbl>Field Name <span className="text-primary normal-case font-normal">(variable)</span></Lbl>
                  <Inp mono value={f.name} onChange={e => set('name', e.target.value)} placeholder="field_name" />
                  <p className="text-[10px] text-ink-500 mt-1 font-sans">Use in emails: {`{${f.name || 'field_name'}}`}</p>
                </Row>

                {!['file', 'signature', 'rating', 'slider', 'hidden', 'radio', 'checkbox'].includes(f.type) && (
                  <MultiLangInp
                    label="Placeholder"
                    value={f.placeholder}
                    onChange={v => setML('placeholder', v)}
                    languages={languages}
                    activeLang={activeLang}
                    placeholder="Placeholder text..."
                    multiline={f.type === 'textarea'}
                  />
                )}

                <MultiLangInp
                  label="Help Text"
                  value={f.helpText}
                  onChange={v => setML('helpText', v)}
                  languages={languages}
                  activeLang={activeLang}
                  placeholder="Optional hint below field"
                  multiline={true}
                />

                {!['file', 'signature', 'rating', 'slider', 'radio', 'checkbox'].includes(f.type) && (
                  <Row>
                    <Lbl>Default Value</Lbl>
                    <Inp value={f.defaultValue ?? ''} onChange={e => set('defaultValue', e.target.value)} maxLength={f.validation?.maxLength} />
                  </Row>
                )}

                {/* Options — multilang */}
                {hasOpts && (
                  <MultiLangOptions
                    options={f.options as FieldOptionML[]}
                    onChange={opts => set('options', opts)}
                    activeLang={activeLang}
                    languages={languages}
                    fieldType={f.type}
                  />
                )}

                {/* Rating */}
                {f.type === 'rating' && (
                  <Row><Lbl>Max Stars</Lbl><Inp type="number" min={1} max={10} value={f.settings.maxStars ?? 5} onChange={e => setS('maxStars', +e.target.value)} /></Row>
                )}

                {/* Slider */}
                {f.type === 'slider' && (
                  <>
                    <SectionTitle>Range Settings</SectionTitle>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div><Lbl>Min</Lbl><Inp type="number" value={f.settings.min ?? 0} onChange={e => setS('min', +e.target.value)} /></div>
                      <div><Lbl>Max</Lbl><Inp type="number" value={f.settings.max ?? 100} onChange={e => setS('max', +e.target.value)} /></div>
                      <div><Lbl>Step</Lbl><Inp type="number" value={f.settings.step ?? 1} onChange={e => setS('step', +e.target.value)} /></div>
                    </div>
                    <Row><Lbl>Unit Suffix (%, kg, $…)</Lbl><Inp value={f.settings.unit ?? ''} onChange={e => setS('unit', e.target.value)} placeholder="%" /></Row>
                    <Toggle checked={!!f.settings.showImages} onChange={v => {
                      setS('showImages', v);
                      if (v && !f.settings.sliderImages?.length) {
                        setS('sliderImages', [{ label: 'Low', img: '', value: 0 }, { label: 'Mid', img: '', value: 50 }, { label: 'High', img: '', value: 100 }]);
                      }
                    }} label="Show image labels at positions" />
                    {f.settings.showImages && (
                      <>
                        <SectionTitle>Image Points</SectionTitle>
                        {(f.settings.sliderImages ?? []).map((img, ii) => (
                          <div key={ii} className="border border-ink-200 rounded-lg p-3 mb-2">
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div><Lbl>Label</Lbl><Inp value={img.label} onChange={e => updSliderImg(ii, 'label', e.target.value)} /></div>
                              <div><Lbl>Value</Lbl><Inp type="number" value={img.value} onChange={e => updSliderImg(ii, 'value', +e.target.value)} /></div>
                            </div>
                            <Lbl>Image URL</Lbl>
                            <Inp value={img.img} onChange={e => updSliderImg(ii, 'img', e.target.value)} placeholder="https://..." />
                            {img.img && <img src={img.img} alt={img.label} className="mt-2 h-12 w-full object-cover rounded-md border border-ink-200" onError={e => (e.currentTarget.style.display = 'none')} />}
                          </div>
                        ))}
                        <button onClick={addSliderImg} className="text-xs text-primary font-medium font-sans">+ Add Image Point</button>
                      </>
                    )}
                  </>
                )}

                {/* File — FIX #4: cloud picker sources added */}
                {f.type === 'file' && (
                  <>
                    <Toggle checked={!!f.settings.multiple} onChange={v => setS('multiple', v)} label="Allow multiple files" />
                    {f.settings.multiple && (
                      <Row><Lbl>Max Files</Lbl><Inp type="number" value={f.settings.maxFiles ?? 3} onChange={e => setS('maxFiles', +e.target.value)} /></Row>
                    )}
                    <Row>
                      <Lbl>Accepted File Types</Lbl>
                      <Sel value={f.settings.accept ?? '*'} onChange={e => setS('accept', e.target.value)}>
                        <option value="*">All files</option>
                        <option value="image/*">Images only</option>
                        <option value="application/pdf">PDF only</option>
                        <option value=".doc,.docx">Word documents</option>
                        <option value="video/*">Videos</option>
                        <option value="audio/*">Audio</option>
                        <option value=".csv,.xlsx,.xls">Spreadsheets</option>
                        <option value=".zip,.rar">Archives</option>
                      </Sel>
                    </Row>
                    <Toggle checked={!!f.settings.showProgress} onChange={v => setS('showProgress', v)} label="Show upload progress bar" />

                    {/* Cloud upload sources */}
                    <SectionTitle>Cloud Upload Sources</SectionTitle>
                    <p className="text-[10px] text-ink-500 font-sans mb-2">
                      Enable extra upload sources shown as buttons below the file drop-zone.
                    </p>
                    <Toggle
                      checked={!!f.settings.enableGoogleDrive}
                      onChange={v => setS('enableGoogleDrive', v)}
                      label="Google Drive"
                      sub="Show a 'Choose from Google Drive' button"
                    />
                    <Toggle
                      checked={!!f.settings.enableDropbox}
                      onChange={v => setS('enableDropbox', v)}
                      label="Dropbox"
                      sub="Show a 'Choose from Dropbox' button"
                    />
                    {(f.settings.enableGoogleDrive || f.settings.enableDropbox) && (
                      <div className="mt-2 rounded-lg bg-primary/10 border border-primary/20 px-3 py-2">
                        <p className="text-[10px] text-primary font-sans font-medium">ℹ API keys required</p>
                        <p className="text-[10px] text-primary/80 font-sans mt-0.5">
                          Set your API keys in <strong>Settings → Integrations → Cloud File Upload</strong>
                        </p>
                      </div>
                    )}
                    {/* Preview of how the upload zone will look */}
                    {(f.settings.enableGoogleDrive || f.settings.enableDropbox) && (
                      <div className="mt-3">
                        <Lbl>Preview</Lbl>
                        <div className="border-2 border-dashed border-ink-200 rounded-md p-3 text-center text-xs text-ink-500 font-sans mb-2">
                          📎 {f.settings.multiple ? 'Drop files or click to upload' : 'Drop file or click to upload'}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {f.settings.enableGoogleDrive && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium font-sans cursor-default"
                              style={{ background: '#e8f0fe', color: '#1a73e8' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M6.18 15L3.75 19h16.5l-2.43-4H6.18zM12 3L2 19h4.75L12 9.5 17.25 19H22L12 3z" /></svg>
                              Google Drive
                            </div>
                          )}
                          {f.settings.enableDropbox && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium font-sans cursor-default"
                              style={{ background: '#e8f4fb', color: '#0061fe' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L6 6.5 12 11 6 15.5 0 11l6-4.5L0 2 6 6.5 12 2zM18 2l-6 4.5L18 11l-6 4.5L18 20l6-4.5L18 11l6-4.5L18 2z" /></svg>
                              Dropbox
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

              </>
            )}
            <SectionTitle>Layout</SectionTitle>
            <Row>
              <Lbl>Column Width</Lbl>
              <div className="flex gap-1.5 mt-1">
                {([
                  {
                    value: 'full', label: 'Full', icon: (
                      <svg width="28" height="12" viewBox="0 0 28 12"><rect x="0" y="0" width="28" height="12" rx="2" fill="currentColor" opacity="0.9" /></svg>
                    )
                  },
                  {
                    value: 'half', label: '1/2', icon: (
                      <svg width="28" height="12" viewBox="0 0 28 12"><rect x="0" y="0" width="12" height="12" rx="2" fill="currentColor" opacity="0.9" /><rect x="16" y="0" width="12" height="12" rx="2" fill="currentColor" opacity="0.3" /></svg>
                    )
                  },
                ] as const).map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set('width', opt.value)}
                    title={opt.label + ' width'}
                    className={`flex flex-col items-center gap-1 px-2.5 py-2 rounded-lg border transition-all font-sans text-[10px] font-semibold ${f.width === opt.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-ink-200 text-ink-500 hover:border-primary/40 hover:text-primary'
                      }`}
                  >
                    <span className={f.width === opt.value ? 'text-primary' : 'text-ink-500'}>
                      {opt.icon}
                    </span>
                    {opt.label}
                  </button>
                ))}
              </div>
              {f.width === 'half' && (
                <p className="text-[10px] text-ink-500 font-sans mt-1.5">
                  💡 Two consecutive half-width fields sit side-by-side in the same row. Works for all field types including headings and paragraphs.
                </p>
              )}
            </Row>
            {!isLayout && <Toggle checked={f.required} onChange={v => set('required', v)} label="Required field" sub="User must fill this before submitting" />}
          </>
        )}

        {/* ── VALIDATION ── */}
        {tab === 'validation' && !isLayout && (
          <>
            {['text', 'email', 'phone', 'url', 'textarea', 'password'].includes(f.type) && (
              <div className="grid grid-cols-2 gap-3">
                <Row><Lbl>Min Length</Lbl><Inp type="number" value={f.validation?.minLength ?? ''} onChange={e => setV('minLength', e.target.value ? +e.target.value : undefined)} placeholder="—" /></Row>
                <Row><Lbl>Max Length</Lbl><Inp type="number" value={f.validation?.maxLength ?? ''} onChange={e => setV('maxLength', e.target.value ? +e.target.value : undefined)} placeholder="—" /></Row>
              </div>
            )}
            {f.type === 'number' && (
              <div className="grid grid-cols-2 gap-3">
                <Row><Lbl>Min Value</Lbl><Inp type="number" value={f.validation?.min ?? ''} onChange={e => setV('min', +e.target.value)} /></Row>
                <Row><Lbl>Max Value</Lbl><Inp type="number" value={f.validation?.max ?? ''} onChange={e => setV('max', +e.target.value)} /></Row>
              </div>
            )}
            {f.type === 'file' && (
              <>
                <Row><Lbl>Max File Size (MB)</Lbl><Inp type="number" value={f.validation?.fileMaxSize ?? ''} onChange={e => setV('fileMaxSize', +e.target.value)} /></Row>
                <Row><Lbl>Allowed Extensions</Lbl><Inp value={(f.validation?.fileTypes ?? []).join(',')} onChange={e => setV('fileTypes', e.target.value.split(',').map(s => s.trim()))} placeholder=".pdf,.jpg,.png" /></Row>
              </>
            )}
            <Row><Lbl>Regex Pattern</Lbl><Inp mono value={f.validation?.pattern ?? ''} onChange={e => setV('pattern', e.target.value)} placeholder="^[A-Z]+$" /></Row>

            {/* ── Multilingual Error Messages ─────────────────────────────── */}
            <div className="mt-1 pt-3 border-t border-ink-200">
              <div className="flex items-center justify-between mb-1.5">
                <Lbl>Custom Error Messages</Lbl>
                {languages.length > 1 && (
                  <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded font-sans font-medium uppercase">{activeLang}</span>
                )}
              </div>
              <p className="text-[10px] text-ink-500 font-sans mb-3 leading-relaxed">
                Override the default English error messages. Leave blank to use the built-in message.
              </p>
              {(() => {
                const msgs = ((f.validation as any)?.messages ?? {}) as Record<string, Record<string, string>>;
                const getMsg = (rule: string) => (msgs[rule] ?? {})[activeLang] ?? '';
                const setMsg = (rule: string, text: string) => {
                  const current = msgs[rule] ?? {};
                  const updated = { ...current, [activeLang]: text };
                  setV('messages', { ...msgs, [rule]: updated });
                };
                type RuleDef = { key: string; label: string; placeholder: string };
                const rules: RuleDef[] = [
                  { key: 'required', label: 'Required', placeholder: 'e.g. This field is required' },
                  ...(['text', 'email', 'phone', 'url', 'textarea', 'password'].includes(f.type) ? [
                    { key: 'minLength', label: 'Too short', placeholder: `e.g. Min ${f.validation?.minLength ?? '?'} characters` },
                    { key: 'maxLength', label: 'Too long', placeholder: `e.g. Max ${f.validation?.maxLength ?? '?'} characters` },
                  ] : []),
                  ...(f.type === 'number' ? [
                    { key: 'min', label: 'Below min', placeholder: `e.g. Min value is ${f.validation?.min ?? '?'}` },
                    { key: 'max', label: 'Above max', placeholder: `e.g. Max value is ${f.validation?.max ?? '?'}` },
                  ] : []),
                  ...(f.type === 'email' ? [{ key: 'email', label: 'Invalid email', placeholder: 'e.g. Enter a valid email' }] : []),
                  ...(f.type === 'url' ? [{ key: 'url', label: 'Invalid URL', placeholder: 'e.g. Enter a valid URL' }] : []),
                  ...(f.type === 'phone' ? [{ key: 'phone', label: 'Invalid phone', placeholder: 'e.g. Enter a valid phone' }] : []),
                  ...(f.validation?.pattern ? [{ key: 'pattern', label: 'Pattern', placeholder: 'e.g. Invalid format' }] : []),
                  ...(f.type === 'file' ? [{ key: 'file', label: 'Invalid file', placeholder: 'e.g. File type not allowed' }] : []),
                  ...(f.type === 'terms_checkbox' ? [{ key: 'required', label: 'Not accepted', placeholder: 'e.g. You must accept the terms' }] : []),
                ];
                return (
                  <div className="space-y-2">
                    {rules.map(rule => (
                      <div key={rule.key} className="flex items-center gap-2">
                        <span className="text-[10px] text-ink-500 font-sans w-20 flex-shrink-0 font-medium">{rule.label}</span>
                        <Inp value={getMsg(rule.key)} onChange={e => setMsg(rule.key, e.target.value)} placeholder={rule.placeholder} className="text-[10px]" />
                      </div>
                    ))}
                    {languages.length > 1 && (
                      <div className="bg-ink-50 rounded-lg px-2.5 py-2 mt-1">
                        <p className="text-[10px] text-ink-500 font-sans">💡 Switch language at the top bar to add translations for each message.</p>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {languages.map(l => {
                            const hasAny = rules.some(r => ((msgs[r.key] ?? {})[l.code] ?? '').trim().length > 0);
                            return (
                              <span key={l.code} className={`text-[9px] px-1.5 py-0.5 rounded-full font-sans font-medium ${hasAny ? 'bg-green-100 text-green-700' : l.code === 'en' ? 'bg-ink-50 text-ink-500' : 'bg-amber-50 text-amber-600'}`}>
                                {hasAny ? '✓' : l.code === 'en' ? '—' : '⚠'} {l.code.toUpperCase()}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {/* ── CONDITIONS ── */}
        {tab === 'conditions' && !isLayout && (
          <>
            <Toggle checked={f.has_conditions} onChange={v => set('has_conditions', v)} label="Enable conditional logic" sub="Show or hide based on other field values" />
            {f.has_conditions && (
              <>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Row>
                    <Lbl>Action</Lbl>
                    <Sel value={f.conditions?.action ?? 'show'} onChange={e => onChange({ ...f, conditions: { ...f.conditions, action: e.target.value as any } })}>
                      <option value="show">Show this field</option>
                      <option value="hide">Hide this field</option>
                    </Sel>
                  </Row>
                  <Row>
                    <Lbl>When</Lbl>
                    <Sel value={f.conditions?.logic ?? 'all'} onChange={e => onChange({ ...f, conditions: { ...f.conditions, logic: e.target.value as any } })}>
                      <option value="all">ALL rules match</option>
                      <option value="any">ANY rule matches</option>
                    </Sel>
                  </Row>
                </div>
                {(f.conditions?.rules ?? []).map((rule, ri) => (
                  <div key={ri} className="border border-ink-200 rounded-lg p-3 mb-2 bg-ink-50/40">
                    <Sel className="mb-2 text-xs" value={rule.field} onChange={e => {
                      const rules = [...(f.conditions?.rules ?? [])];
                      rules[ri] = { ...rules[ri], field: e.target.value };
                      onChange({ ...f, conditions: { ...f.conditions, rules } });
                    }}>
                      <option value="">Select field...</option>
                      {allFields.filter(fi => fi._id !== f._id).map(fi => (
                        <option key={fi._id} value={fi.name}>{mlGet(fi.label, 'en')}</option>
                      ))}
                    </Sel>
                    <div className="flex gap-2">
                      <Sel className="flex-1 text-xs" value={rule.operator} onChange={e => {
                        const rules = [...(f.conditions?.rules ?? [])];
                        rules[ri] = { ...rules[ri], operator: e.target.value as any };
                        onChange({ ...f, conditions: { ...f.conditions, rules } });
                      }}>
                        <option value="equals">equals</option>
                        <option value="not_equals">does not equal</option>
                        <option value="contains">contains</option>
                        <option value="not_contains">does not contain</option>
                        <option value="is_empty">is empty</option>
                        <option value="is_not_empty">is not empty</option>
                      </Sel>
                      {!['is_empty', 'is_not_empty'].includes(rule.operator) && (
                        <Inp className="flex-1 text-xs" value={rule.value} onChange={e => {
                          const rules = [...(f.conditions?.rules ?? [])];
                          rules[ri] = { ...rules[ri], value: e.target.value };
                          onChange({ ...f, conditions: { ...f.conditions, rules } });
                        }} placeholder="value..." />
                      )}
                      <button onClick={() => {
                        const rules = (f.conditions?.rules ?? []).filter((_, j) => j !== ri);
                        onChange({ ...f, conditions: { ...f.conditions, rules } });
                      }} className="p-1 hover:bg-red-50 rounded">
                        <Trash2 className="h-3 w-3 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
                <button onClick={() => onChange({ ...f, conditions: { ...f.conditions, rules: [...(f.conditions?.rules ?? []), { field: '', operator: 'equals', value: '' }] } })}
                  className="text-xs text-primary font-medium font-sans flex items-center gap-1 mt-1">
                  + Add Condition
                </button>
              </>
            )}
          </>
        )}

        {/* ── ADVANCED ── */}
        {tab === 'advanced' && !isLayout && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Row><Lbl>CSS Class</Lbl><Inp mono value={f.cssClass ?? ''} onChange={e => set('cssClass', e.target.value)} placeholder="my-class" /></Row>
              <Row><Lbl>CSS ID</Lbl><Inp mono value={f.cssId ?? ''} onChange={e => set('cssId', e.target.value)} placeholder="my-id" /></Row>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Field Canvas Card ────────────────────────────────────────────────────────

interface FieldCardProps {
  field: FormField;
  index: number;
  selected: boolean;
  onSelect: () => void;
  onChange: (f: FormField) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  dragStart: (e: React.DragEvent) => void;
  dragOver: (e: React.DragEvent) => void;
  drop: (e: React.DragEvent) => void;
  dragEnd: () => void;
  previewLang: string; // ← render field in this language
}

export const FieldSettingsPanel = React.memo(FieldSettingsPanelInner);
FieldSettingsPanel.displayName = "FieldSettingsPanel";

const FieldCardInner: React.FC<FieldCardProps> = ({
  field: f, index, selected, onSelect, onDelete, onDuplicate,
  onMoveUp, onMoveDown, isFirst, isLast,
  dragStart, dragOver, drop, dragEnd, previewLang,
}) => {
  const isLayout = LAYOUT_TYPES.includes(f.type);
  const label = mlGet(f.label, previewLang);
  const helpText = mlGet(f.helpText, previewLang);

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.currentTarget.classList.add('scale-[1.02]', 'shadow-xl', 'z-50');
        dragStart(e);
      }}
      onDragOver={dragOver}
      onDrop={drop}
      onDragEnd={(e) => {
        e.currentTarget.classList.remove('scale-[1.02]', 'shadow-xl', 'z-50');
        dragEnd();
      }}
      onClick={onSelect}
      className={`group relative rounded-xl transition-all cursor-pointer border ${selected
        ? 'border-brand-500 ring-2 ring-brand-500/20 bg-brand-50 shadow-sm z-10'
        : 'border-transparent bg-white hover:border-ink-200 hover:shadow-sm'
        }`}
    >
      <div className={`p-5 rounded-xl border ${selected ? 'border-transparent' : 'border-ink-200'}`}>
        {isLayout ? (
          <div>
            {f.type === 'heading' && <p className="text-lg font-bold text-ink-900 font-sans tracking-tight">{label || 'Heading'}</p>}
            {f.type === 'paragraph' && <p className="text-sm text-ink-500 font-sans leading-relaxed">{label || 'Paragraph text'}</p>}
            {f.type === 'divider' && (
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-ink-200" />
                <span className="flex-shrink-0 whitespace-nowrap flex items-center gap-1.5 text-[10px] font-bold text-ink-600 bg-white border border-ink-200 shadow-sm px-3 py-1.5 rounded-full font-sans uppercase tracking-wider">
                  {f.settings.sectionIcon && <span className="normal-case">{f.settings.sectionIcon}</span>}
                  {f.settings.sectionLabel || 'Section Break'}
                </span>
                <div className="flex-1 h-px bg-ink-200" />
              </div>
            )}
            {f.type === 'spacer' && <div className="bg-canvas border border-dashed border-ink-200 rounded-lg text-center py-2 text-[10px] font-medium text-ink-500">Spacer ({f.settings.height ?? 20}px)</div>}
            {f.type === 'html' && <div className="text-xs text-brand-600 border border-dashed border-brand-200 bg-brand-50 rounded-lg px-3 py-2 font-mono">HTML Block</div>}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-ink-900 font-sans">
                {label || <span className="italic text-ink-500">Untitled {f.type}</span>}
              </span>
              {f.required && <span className="text-danger-600 text-sm">*</span>}
              <span className="ml-auto text-[10px] font-medium bg-ink-50 text-ink-500 border border-ink-200 px-2 py-0.5 rounded-md font-mono uppercase tracking-tight">{f.type}</span>
              {f.width === 'half' && <span className="text-[10px] font-medium text-brand-700 bg-brand-100 border border-brand-200 px-2 py-0.5 rounded-md font-sans">½ width</span>}
              {f.has_conditions && <span className="text-[10px] font-medium text-warning-700 bg-warning-100 border border-warning-200 px-2 py-0.5 rounded-md">conditional</span>}
            </div>
            <FieldPreview f={f} lang={previewLang} />
            {helpText && <p className="text-[11px] text-ink-500 mt-2 font-sans leading-snug">{helpText}</p>}
          </div>
        )}
      </div>

      {/* Floating Hover actions */}
      <div className={`absolute -top-3 right-4 flex items-center gap-0.5 bg-surface border border-ink-200 rounded-lg shadow-md p-1 transition-opacity duration-200 ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <div className="flex items-center justify-center p-1.5 cursor-grab active:cursor-grabbing hover:bg-ink-100 rounded-md transition-colors mr-1">
          <GripVertical className="h-4 w-4 text-ink-400" />
        </div>
        {!isFirst && <button onClick={e => { e.stopPropagation(); onMoveUp(); }} className="p-1.5 rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100 transition-colors"><ChevronUp className="h-4 w-4" /></button>}
        {!isLast && <button onClick={e => { e.stopPropagation(); onMoveDown(); }} className="p-1.5 rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100 transition-colors"><ChevronDown className="h-4 w-4" /></button>}
        <div className="w-px h-4 bg-ink-200 mx-1" />
        <button onClick={e => { e.stopPropagation(); onDuplicate(); }} className="p-1.5 rounded-md text-ink-500 hover:text-brand-600 hover:bg-brand-50 transition-colors"><Copy className="h-4 w-4" /></button>
        <button onClick={e => { e.stopPropagation(); onDelete(); }} className="p-1.5 rounded-md text-ink-500 hover:text-danger-600 hover:bg-danger-50 transition-colors"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const FieldCard = React.memo(FieldCardInner);
FieldCard.displayName = "FieldCard";